import React, { useRef, useState } from "react";
import { Camera, Upload, CheckCircle2, AlertTriangle, Loader2, ShieldCheck, X } from "lucide-react";
import { api, fmtPoints } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";

export default function ScanPage() {
    const fileRef = useRef(null);
    const camRef = useRef(null);
    const { refresh } = useAuth();
    const [preview, setPreview] = useState(null);
    const [b64, setB64] = useState(null);
    const [mime, setMime] = useState("image/jpeg");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState("");

    const onFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!/^(image\/(jpe?g|png|webp|heic|heif)|application\/pdf)$/i.test(f.type)) {
            setErr("Please upload a JPEG, PNG, WEBP, HEIC image or PDF");
            return;
        }
        setErr("");
        setResult(null);
        setMime(f.type);
        const r = new FileReader();
        r.onload = () => {
            setPreview(r.result);
            setB64(r.result.split(",")[1]);
        };
        r.readAsDataURL(f);
    };

    const submit = async () => {
        if (!b64) return;
        setBusy(true);
        setErr("");
        try {
            const r = await api.post("/api/bills/upload-file", { file_base64: b64, mime_type: mime, source: "web" });
            setResult(r.data);
            await refresh();
        } catch (e) {
            setErr(e.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    const reset = () => {
        setPreview(null);
        setB64(null);
        setResult(null);
        setErr("");
    };

    return (
        <div className="pb-28">
            <header className="px-5 pt-7">
                <h1 className="font-display text-2xl font-bold">Scan a bill</h1>
                <p className="text-[#6b7280] text-sm">Snap or upload — AI verifies & rewards instantly.</p>
            </header>

            {!preview && !result && (
                <div className="px-5 mt-6">
                    <label
                        htmlFor="bill-file"
                        data-testid="bill-upload-zone"
                        className="block border-2 border-dashed border-[rgba(11,20,55,0.15)] rounded-3xl p-10 bg-white/[0.03] text-center cursor-pointer hover:border-[#c9a959]/40 transition-all"
                    >
                        <div className="w-16 h-16 mx-auto rounded-2xl bg-[#c9a959]/15 text-[#c9a959] flex items-center justify-center mb-4">
                            <Upload size={28} />
                        </div>
                        <p className="font-semibold">Tap to upload a bill</p>
                        <p className="text-xs text-[#9ca3af] mt-1">JPEG / PNG / WEBP / HEIC / PDF up to 8 MB</p>
                        <input
                            id="bill-file"
                            ref={fileRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
                            onChange={onFile}
                            className="hidden"
                            data-testid="bill-file-input"
                        />
                    </label>

                    <button
                        data-testid="capture-bill-btn"
                        onClick={() => camRef.current?.click()}
                        className="fx-btn-ghost w-full mt-3"
                    >
                        <Camera size={16} /> Use camera
                    </button>
                    <input
                        ref={camRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={onFile}
                        className="hidden"
                    />

                    <div className="fx-card mt-6 p-4 flex items-start gap-3">
                        <ShieldCheck className="text-[#c9a959] mt-0.5" size={20} />
                        <div className="text-xs text-[#6b7280]">
                            Our AI checks merchant, GST, invoice number, math integrity and fraud signals
                            (duplicates, tampering, screenshots) — all in under 3 seconds.
                        </div>
                    </div>
                </div>
            )}

            {preview && !result && (
                <div className="px-5 mt-6">
                    <div className="relative rounded-3xl overflow-hidden border border-[rgba(11,20,55,0.10)] fx-card p-2">
                        {mime === "application/pdf" ? (
                            <div className="w-full aspect-[3/4] max-h-[420px] bg-[#f9fafb] rounded-2xl flex flex-col items-center justify-center gap-2 text-[#0b1437]">
                                <Upload size={36} className="text-[#c9a959]" />
                                <p className="font-display font-bold">PDF bill ready</p>
                                <p className="text-xs text-[#6b7280]">{b64 ? `${Math.round(b64.length * 0.75 / 1024)} KB` : ""}</p>
                            </div>
                        ) : (
                            <img src={preview} alt="bill" className="w-full max-h-[420px] object-contain rounded-2xl" />
                        )}
                        {busy && (
                            <>
                                <div className="absolute inset-0 bg-black/40 rounded-2xl pointer-events-none" />
                                <div className="absolute inset-x-2 top-2 bottom-2 overflow-hidden rounded-2xl pointer-events-none">
                                    <div
                                        className="fx-anim-scan absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-[#ccff00] to-transparent shadow-[0_0_30px_rgba(201,169,89,0.7)]"
                                    />
                                </div>
                            </>
                        )}
                        <button
                            onClick={reset}
                            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur flex items-center justify-center"
                        >
                            <X size={16} />
                        </button>
                    </div>
                    <button
                        data-testid="submit-bill-btn"
                        onClick={submit}
                        disabled={busy}
                        className="fx-btn-primary w-full mt-4"
                    >
                        {busy ? (
                            <>
                                <Loader2 className="animate-spin" size={16} /> AI analysing your bill…
                            </>
                        ) : (
                            "Verify & Earn Points"
                        )}
                    </button>
                    {err && <p className="text-red-400 mt-3 text-sm text-center">{err}</p>}
                </div>
            )}

            {result && <ResultCard result={result} preview={preview} onAgain={reset} />}
        </div>
    );
}

function ResultCard({ result, preview, onAgain }) {
    const ok = result.status === "approved";
    const mod = result.status === "moderation";
    const rej = result.status === "rejected";

    const headerColor = ok
        ? "from-[#ccff00]/20 to-transparent border-[#c9a959]/40"
        : mod
        ? "from-[#ffd700]/20 to-transparent border-[#ffd700]/40"
        : "from-[#ff4d4d]/20 to-transparent border-[#ff4d4d]/40";
    const Icon = ok ? CheckCircle2 : mod ? AlertTriangle : X;
    const titleColor = ok ? "text-[#c9a959]" : mod ? "text-[#ffd700]" : "text-[#ff4d4d]";

    return (
        <div className="px-5 mt-6 fx-anim-rise">
            <div className={`rounded-3xl border bg-gradient-to-b ${headerColor} p-5`}>
                <div className="flex items-center gap-3">
                    <Icon size={22} className={titleColor} />
                    <p className={`font-display text-xl font-bold ${titleColor}`} data-testid="bill-status">
                        {ok ? "Bill approved!" : mod ? "Sent to review" : "Bill rejected"}
                    </p>
                </div>
                {ok && (
                    <div className="mt-4">
                        <p className="font-display text-5xl font-extrabold">
                            +{result.points_awarded}
                            <span className="text-[#c9a959] text-2xl ml-2">pts</span>
                        </p>
                        <p className="text-[#4b5563] text-sm mt-1">Credited to your wallet 🎉</p>
                    </div>
                )}
                {(mod || rej) && (
                    <p className="text-[#374151] text-sm mt-3">
                        {result.rejection_reason || result.summary || "Our team will review shortly."}
                    </p>
                )}
            </div>

            <div className="fx-card mt-4 p-5">
                <p className="text-xs uppercase tracking-widest text-[#6b7280] mb-3">Extracted by AI</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <KV k="Merchant" v={result.extracted?.merchant_name} />
                    <KV k="Invoice" v={result.extracted?.invoice_number} />
                    <KV k="Date" v={result.extracted?.invoice_date} />
                    <KV k="GSTIN" v={result.extracted?.gst_number} />
                    <KV k="Subtotal" v={result.extracted?.subtotal && `₹${result.extracted.subtotal}`} />
                    <KV k="Tax" v={result.extracted?.tax && `₹${result.extracted.tax}`} />
                    <KV k="Total" v={result.extracted?.total && `₹${result.extracted.total}`} />
                    <KV k="Items" v={result.extracted?.items?.length ? `${result.extracted.items.length} line(s)` : "—"} />
                </div>
            </div>

            <div className="fx-card mt-3 p-5">
                <p className="text-xs uppercase tracking-widest text-[#6b7280] mb-3">Fraud signals</p>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <p className="text-[11px] text-[#6b7280]">Fraud score</p>
                        <p className="font-display text-2xl font-bold" data-testid="bill-fraud-score">
                            {Math.round(result.fraud_score || 0)}
                            <span className="text-[#9ca3af] text-base">/100</span>
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-[11px] text-[#6b7280]">AI confidence</p>
                        <p className="font-display text-2xl font-bold text-[#c9a959]" data-testid="bill-confidence">
                            {Math.round(result.confidence_score || 0)}%
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {Object.entries(result.fraud_signals || {})
                        .filter(([k, v]) => k !== "notes" && v === true)
                        .map(([k]) => (
                            <span
                                key={k}
                                className="fx-chip bg-[#ff4d4d]/15 text-[#ff4d4d] border border-[#ff4d4d]/30"
                            >
                                {k.replace(/_/g, " ")}
                            </span>
                        ))}
                    {(!result.fraud_signals ||
                        Object.entries(result.fraud_signals)
                            .filter(([k, v]) => k !== "notes" && v === true).length === 0) && (
                        <span className="fx-chip bg-[#c9a959]/10 text-[#c9a959] border border-[#c9a959]/30">
                            Clean
                        </span>
                    )}
                </div>
                {result.fraud_signals?.notes?.length > 0 && (
                    <ul className="text-xs text-[#6b7280] mt-3 space-y-1 list-disc list-inside">
                        {result.fraud_signals.notes.map((n, i) => (
                            <li key={i}>{n}</li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
                <button onClick={onAgain} className="fx-btn-ghost" data-testid="scan-another-btn">
                    Scan another
                </button>
                <Link to="/app/wallet" className="fx-btn-primary" data-testid="open-wallet-btn">
                    Open wallet
                </Link>
            </div>
        </div>
    );
}

function KV({ k, v }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-widest text-[#9ca3af]">{k}</p>
            <p className="text-[#0b1437] truncate">{v || "—"}</p>
        </div>
    );
}
